import {
  adminLegalDocumentCatalogItemSchema,
  adminLegalDocumentCatalogSchema,
  adminLegalDocumentVersionSchema,
  legalDocumentHistoryPageSchema,
  legalDocumentHistoryQuerySchema,
  legalDocumentPublishInputSchema,
  legalDocumentUploadMetadataSchema,
  paymentReceiptInputSchema,
} from "@ai-crm/shared";
import type {
  LegalDocumentKey,
  LegalDocumentPublishInput,
  LegalDocumentUploadMetadata,
} from "@ai-crm/shared";
import type {
  AdminInvoiceDetailDTO, AdminPaymentsPageDTO, PaymentPayersPageDTO,
  PaymentRecordInput, PaymentCorrectionInput, PaymentCommandResultDTO,
  InvoiceApplicationPreviewInput, InvoiceUpgradePreviewInput, InvoiceApplicationPreviewDTO,
  InvoiceApplicationConfirmInput, PaymentReviewPreviewInput, PaymentReviewPreviewDTO,
  PaymentReviewConfirmInput, PaymentReviewResultDTO, PaymentReceiptInput, PaymentDocumentDTO,
  PaymentRecipientVersionDTO, PaymentRecipientPreviewInput, PaymentRecipientPreviewDTO,
  PaymentRecipientConfirmInput, InvoiceListItemDTO,
} from "@ai-crm/shared";
import type {
  FollowupPreviewInput,
  FollowupPreviewDTO,
  WabaTemplateListDTO,
} from "@ai-crm/shared";
import type { FollowupSettingsDTO, SaveFollowups } from "@ai-crm/shared";
import type { CrmCatalogPreview } from "@ai-crm/shared";
import type { AgentActionDTO, ProposedAgentAction } from "@ai-crm/shared";
import type {
  FunnelPromptFields,
  FunnelPromptInput,
  PromptBuilderInput,
  PromptGenerationCandidate,
  EscalationGenerationInput,
  EscalationGenerationCandidate,
} from "@ai-crm/shared";
import type {
  PlatformPromptKey,
  PlatformPromptListItem,
  PromptSnapshot,
  PlatformPromptRevisionPage,
} from "@ai-crm/shared";
import type {
  AdminBillingViewDTO,
  BillingTariffChangeInput,
  BillingTariffChangePreviewDTO,
  BillingTariffChangeResultDTO,
  BillingConfirmInput,
  BillingConfirmResultDTO,
  BillingPreviewDTO,
  BillingPreviewInput,
  TariffCatalogConfirmInput,
  TariffCatalogDTO,
  TariffCatalogPreviewDTO,
  TariffCatalogPreviewInput,
  TariffIdentityDTO,
  BillingSummaryDTO,
} from "@ai-crm/shared";

const productionApiUrl = "https://api.kashtn-auto-n8n.ru";
const localApiUrl = "http://localhost:3002";

const domainApiUrls = [
  { suffix: "whatsai.ru", apiUrl: "https://api.whatsai.ru" },
  { suffix: "kashtn-auto-n8n.ru", apiUrl: productionApiUrl },
];

function inferApiUrl() {
  if (typeof window === "undefined") return localApiUrl;
  const match = domainApiUrls.find(({ suffix }) =>
    window.location.hostname.endsWith(suffix),
  );
  return match?.apiUrl ?? localApiUrl;
}

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? inferApiUrl();

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Check actual UTF-8 multipart metadata bytes, independently of PDF size. */
export function serializePaymentReceiptMetadata(input: PaymentReceiptInput): string {
  const metadata = JSON.stringify(input);
  if (new TextEncoder().encode(metadata).byteLength > 16 * 1024) {
    throw new ApiError("Данные загрузки чека превышают 16 КиБ. Сократите причину замены.", 413, "PAYMENT_RECEIPT_METADATA_TOO_LARGE");
  }
  const parsed = paymentReceiptInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new ApiError("Проверьте данные загрузки: причина должна быть не длиннее 4000 символов и не содержать управляющих символов.", 400, "PAYMENT_RECEIPT_METADATA_INVALID");
  }
  return JSON.stringify(parsed.data);
}

export interface AdminUser {
  id: string;
  username: string;
  createdAt: string;
}

export interface AuthResponse {
  token: string;
  admin: AdminUser;
}

export interface LlmUsage {
  model: string | null;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  cachedTokens: number;
  costUsdTicks: string | null;
  costUsd: number | null;
}

export type AccountStatus = "ACTIVE" | "SUSPENDED";
export type LlmModel =
  "GPT4O_MINI" | "GPT4O" | "CLAUDE_HAIKU" | "CLAUDE_SONNET";
export type ChannelType = "TELEGRAM" | "WHATSAPP";

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AdminAccountUser {
  id: string;
  phone: string;
  email: string;
  updatedAt: string;
  mustChangePassword: boolean;
}

export interface AdminAccount {
  id: string;
  amoAccountId: string;
  tariff: TariffIdentityDTO;
  billingSummary: BillingSummaryDTO;
  status: AccountStatus;
  suspensionReason: string | null;
  timeZone: string;
  settingsVersion: number;
  createdAt: string;
  updatedAt: string;
  user: AdminAccountUser | null;
  funnelsCount: number;
  dialogsCount: number;
  lastRunAt: string | null;
  llmUsage: LlmUsage;
  amoConnected: boolean;
  wazzupConnected: boolean;
}

export interface AdminAccountSettings {
  id: string;
  status: AccountStatus;
  suspensionReason: string | null;
  timeZone: string;
  settingsVersion: number;
  updatedAt: string;
}

export interface AdminChannel {
  id: string;
  funnelId?: string;
  wazzupChannelId: string;
  type: ChannelType;
}

export interface AdminFunnelSettings extends FunnelPromptFields {
  id: string;
  accountId: string;
  amoPipelineId: string;
  amoStatusIds: string[];
  name: string;
  systemPrompt: string;
  model: LlmModel;
  stopPhrase: string | null;
  escalateOnGoal?: boolean;
  goalStatusId?: string | null;
  expectedCapabilitiesVersion?: number;
  outcomeCriteriaStatus?: "PENDING" | "READY" | "UNDEFINED";
  allowLeadUpdates?: boolean;
  allowContactUpdates?: boolean;
  escalationEnabled?: boolean;
  escalationStatusId?: string | null;
  capabilitiesVersion?: number;
  escalate: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminFunnel extends AdminFunnelSettings {
  channels: AdminChannel[];
  dialogsCount: number;
  runsCount: number;
  llmUsage: LlmUsage;
  account?: {
    id: string;
    amoAccountId: string;
    user: { id: string; email: string } | null;
  };
}

export interface AdminIntegrationStatus {
  amoConnected: boolean;
  wazzupConnected: boolean;
}

export interface AdminPipeline {
  id: string;
  name: string;
  bound: boolean;
}

export interface AdminPipelineStatus {
  id: string;
  name: string;
  color?: string;
  sort: number;
}

export interface AdminWazzupChannel {
  id: string;
  name: string;
  type: ChannelType;
  boundFunnelIds: string[];
}

export interface AdminDialogListItem {
  id: string;
  accountId: string;
  funnel: { id: string; name: string };
  contactId: string;
  contactName: string | null;
  amoContactId: string | null;
  amoLeadId: string | null;
  channelId: string;
  messagesCount: number;
  lastMessage: {
    role: "USER" | "ASSISTANT";
    preview: string;
    createdAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminMessage {
  id: string;
  role: "USER" | "ASSISTANT";
  content: string;
  model: string | null;
  tokens: number | null;
  latencyMs: number | null;
  deliveryStatus:
    | "NOT_APPLICABLE"
    | "PENDING"
    | "ACCEPTED"
    | "SENT"
    | "DELIVERED"
    | "READ"
    | "UNKNOWN"
    | "FAILED";
  createdAt: string;
}

export type JsonValue =
  null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

export interface AdminAuditEntry {
  id: string;
  adminUsername: string;
  action: string;
  targetType: string;
  targetId: string;
  before: JsonValue;
  after: JsonValue;
  metadata: JsonValue;
  createdAt: string;
}

export interface AdminAccountsQuery {
  search?: string;
  tariffId?: string;
  status?: AccountStatus;
  cursor?: string;
  take?: number;
}

export interface UpdateAccountProfileInput {
  expectedUserUpdatedAt: string;
  email: string;
}

export interface ExpectedAccountVersionInput {
  expectedSettingsVersion: number;
}

export interface SuspendAccountInput extends ExpectedAccountVersionInput {
  reason: string;
}

export interface TemporaryPasswordInput {
  expectedUserUpdatedAt: string;
  temporaryPassword: string;
}

export interface CreateAdminFunnelInput extends FunnelPromptInput {
  amoPipelineId: string;
  amoStatusIds: string[];
  name: string;
  systemPrompt: string;
  model: LlmModel;
  stopPhrase: string | null;
  escalateOnGoal?: boolean;
  goalStatusId?: string | null;
  expectedCapabilitiesVersion?: number;
  outcomeCriteriaStatus?: "PENDING" | "READY" | "UNDEFINED";
  allowLeadUpdates?: boolean;
  allowContactUpdates?: boolean;
  escalationEnabled?: boolean;
  escalationStatusId?: string | null;
  capabilitiesVersion?: number;
  escalate: boolean;
  active: boolean;
}

export interface UpdateAdminFunnelInput extends FunnelPromptInput {
  expectedUpdatedAt: string;
  name?: string;
  amoStatusIds?: string[];
  systemPrompt?: string;
  model?: LlmModel;
  stopPhrase?: string | null;
  escalateOnGoal?: boolean;
  goalStatusId?: string | null;
  expectedCapabilitiesVersion?: number;
  outcomeCriteriaStatus?: "PENDING" | "READY" | "UNDEFINED";
  allowLeadUpdates?: boolean;
  allowContactUpdates?: boolean;
  escalationEnabled?: boolean;
  escalationStatusId?: string | null;
  escalate?: boolean;
  active?: boolean;
}

export interface DeleteAdminFunnelInput {
  expectedUpdatedAt: string;
  confirmationName: string;
}

export interface BindAdminChannelInput {
  wazzupChannelId: string;
}

export interface TestAdminFunnelInput {
  message: string;
}

export interface TestAdminFunnelResult {
  proposedActions?: ProposedAgentAction[];
  reply: string;
  model: string;
  tokensUsed: number;
}

export interface CursorQuery {
  cursor?: string;
  take?: number;
}

export interface AdminDialogsQuery extends CursorQuery {
  funnelId?: string;
  channelId?: string;
  contact?: string;
  from?: string;
  to?: string;
}

export interface PipelineRun {
  resultDisplay?: { label: string; description: string } | null;
  id: string;
  accountId: string;
  funnelId: string | null;
  dialogId: string | null;
  chatId: string;
  chatType: string;
  channelId: string;
  wazzupMessageId: string;
  inboundText: string;
  inboundPreview?: string;
  deliveryStatus?: RunDeliveryStatus;
  processingDisplay?: StatusDisplay;
  deliveryDisplay?: StatusDisplay;
  senderPhone?: string | null;
  amoLeadId?: string | null;
  status: "RUNNING" | "DONE" | "SKIPPED" | "FAILED";
  errorStep: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  llmUsage: LlmUsage;
  eventsCount?: number;
  account?: { id: string; amoAccountId: string };
  funnel?: { id: string; name: string } | null;
}

export type RunDeliveryStatus =
  | "NOT_STARTED"
  | "PENDING"
  | "ACCEPTED"
  | "SENT"
  | "DELIVERED"
  | "READ"
  | "UNKNOWN"
  | "FAILED";

export interface StatusDisplay {
  label: string;
  description: string;
}

export interface PipelineEvent {
  attachments?: Array<{ label: string; content: string }>;
  id: string;
  runId: string;
  sequence: string;
  step: string;
  level: "INFO" | "WARN" | "ERROR";
  title: string;
  summary: string;
  display: {
    title: string;
    description: string;
    operatorHint: string | null;
  };
  details: unknown | null;
  raw: unknown | null;
  occurredAt: string;
  createdAt: string;
}

export interface PipelineRunDetail extends PipelineRun {
  activityPending?: boolean;
  account: { id: string; amoAccountId: string };
  funnel: { id: string; name: string } | null;
  processingDisplay: StatusDisplay;
  sender: {
    name: string | null;
    phone: string | null;
    username: string | null;
    chatId: string;
    chatType: string;
  };
  amo: {
    contactId: string | null;
    leadId: string | null;
    pipelineId: string | null;
  };
  batch: {
    id: string;
    status: string;
    revision: number;
    processAfter: string;
    messages: Array<{
      id: string;
      wazzupMessageId: string;
      text: string | null;
      senderName: string | null;
      senderPhone: string | null;
      senderUsername: string | null;
      messageType: string | null;
      hasContentUri: boolean;
      receivedAt: string;
      sourceDateTime: string | null;
    }>;
  } | null;
  llmRequest: {
    capturedAt: string | null;
    messages: Array<{ role: "system" | "user" | "assistant"; content: string }>;
  } | null;
  llmRequestCapturedAt: string | null;
  llmResponse: {
    messageId: string;
    content: string;
    provider: string | null;
    model: string | null;
    tokens: number | null;
    latencyMs: number | null;
  } | null;
  llmRouting: {
    primaryProvider: string | null;
    finalProvider: string | null;
    model: string | null;
    attempts: number;
    fallbackUsed: boolean;
  };
  delivery: {
    status: RunDeliveryStatus;
    display: StatusDisplay;
    wazzupMessageId: string | null;
    updatedAt: string | null;
    error: { code: string | null; description: string | null } | null;
  };
  diagnosticConclusion: {
    code: string;
    title: string;
    description: string;
    lastSuccessfulStage: {
      step: string;
      title: string;
      occurredAt: string;
    } | null;
    problemStage: { step: string; title: string; occurredAt: string } | null;
  };
  events: PipelineEvent[];
}

export interface AdminRunsQuery {
  chatId?: string;
  senderPhone?: string;
  messageId?: string;
  amoLeadId?: string;
  status?: PipelineRun["status"];
  deliveryStatus?: RunDeliveryStatus;
  errorStep?: string;
  from?: string;
  to?: string;
  take?: number;
}

function queryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : "";
}

function pathSegment(value: string): string {
  return encodeURIComponent(value);
}

async function requestResponse(
  path: string,
  token: string | null,
  options: RequestInit = {},
): Promise<Response> {
  const hasBody = typeof options.body === "string";
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).error ?? res.statusText,
      res.status,
      (body as Record<string, string>).code ?? "UNKNOWN",
    );
  }

  return res;
}

async function request<T>(
  path: string,
  token: string | null,
  options: RequestInit = {},
): Promise<T> {
  const res = await requestResponse(path, token, options);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function requestBlob(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<Blob> {
  return (await requestResponse(path, token, options)).blob();
}

export interface PaymentPageQuery {
  cursor?: string;
  limit?: number;
}
export interface PaymentInvoicesQuery extends PaymentPageQuery {
  status?: InvoiceListItemDTO["status"];
  number?: string;
}
function paymentAccount(id: string): string {
  return `/admin/accounts/${pathSegment(id)}`;
}
function paymentInvoice(id: string, invoiceId: string): string {
  return `${paymentAccount(id)}/invoices/${pathSegment(invoiceId)}`;
}
function paymentRecord(id: string, paymentId: string): string {
  return `${paymentAccount(id)}/payments/${pathSegment(paymentId)}`;
}
function jsonPost(input: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(input) };
}

function accounts(token: string): Promise<AdminAccount[]>;
function accounts(
  token: string,
  query: AdminAccountsQuery,
): Promise<CursorPage<AdminAccount>>;
async function accounts(
  token: string,
  query?: AdminAccountsQuery,
): Promise<AdminAccount[] | CursorPage<AdminAccount>> {
  if (query !== undefined) {
    return request<CursorPage<AdminAccount>>(
      `/admin/accounts${queryString(query)}`,
      token,
    );
  }

  const items: AdminAccount[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  do {
    const page: CursorPage<AdminAccount> = await request<
      CursorPage<AdminAccount>
    >(`/admin/accounts${queryString({ cursor })}`, token);
    items.push(...page.items);
    cursor = page.nextCursor;

    if (cursor !== null) {
      if (cursor === "" || seenCursors.has(cursor)) {
        throw new ApiError(
          "Некорректный курсор пагинации аккаунтов",
          500,
          "INVALID_PAGINATION_CURSOR",
        );
      }
      seenCursors.add(cursor);
    }
  } while (cursor !== null);

  return items;
}

export const adminApi = {
  legalDocuments: async (token: string, signal?: AbortSignal) =>
    adminLegalDocumentCatalogSchema.parse(
      await request<unknown>("/admin/legal-documents", token, { signal }),
    ),
  legalDocumentVersions: async (
    token: string,
    key: LegalDocumentKey,
    query: PaymentPageQuery = {},
    signal?: AbortSignal,
  ) => {
    const parsed = legalDocumentHistoryQuerySchema.parse(query);
    return legalDocumentHistoryPageSchema.parse(
      await request<unknown>(
        `/admin/legal-documents/${pathSegment(key)}/versions${queryString(parsed)}`,
        token,
        { signal },
      ),
    );
  },
  uploadLegalDocument: async (
    token: string,
    key: LegalDocumentKey,
    metadata: LegalDocumentUploadMetadata,
    file: File,
  ) => {
    const body = new FormData();
    body.append("metadata", JSON.stringify(legalDocumentUploadMetadataSchema.parse(metadata)));
    body.append("file", file);
    return adminLegalDocumentVersionSchema.parse(
      await request<unknown>(
        `/admin/legal-documents/${pathSegment(key)}/versions`,
        token,
        { method: "POST", body },
      ),
    );
  },
  publishLegalDocument: async (
    token: string,
    key: LegalDocumentKey,
    input: LegalDocumentPublishInput,
  ) => adminLegalDocumentCatalogItemSchema.parse(
    await request<unknown>(
      `/admin/legal-documents/${pathSegment(key)}/publish`,
      token,
      jsonPost(legalDocumentPublishInputSchema.parse(input)),
    ),
  ),
  legalDocumentVersionPdf: (
    token: string,
    key: LegalDocumentKey,
    versionId: string,
    signal?: AbortSignal,
  ) => requestBlob(
    `/admin/legal-documents/${pathSegment(key)}/versions/${pathSegment(versionId)}/pdf`,
    token,
    { signal },
  ),
  accountPayments: (
    token: string,
    id: string,
    query: PaymentInvoicesQuery = {},
    signal?: AbortSignal,
  ) =>
    request<AdminPaymentsPageDTO>(
      `${paymentAccount(id)}/payments${queryString({ ...query })}`,
      token,
      { signal },
    ),
  paymentPayers: (
    token: string,
    id: string,
    query: PaymentPageQuery & { archived?: "active" | "archived" | "all" } = {},
    signal?: AbortSignal,
  ) =>
    request<PaymentPayersPageDTO>(
      `${paymentAccount(id)}/payment-payers${queryString({ ...query })}`,
      token,
      { signal },
    ),
  invoice: (
    token: string,
    id: string,
    invoiceId: string,
    signal?: AbortSignal,
  ) =>
    request<AdminInvoiceDetailDTO>(paymentInvoice(id, invoiceId), token, {
      signal,
    }),
  retryInvoice: (
    token: string,
    id: string,
    invoiceId: string,
    input: { requestId: string; expectedVersion: number },
  ) =>
    request<AdminInvoiceDetailDTO>(
      `${paymentInvoice(id, invoiceId)}/retry`,
      token,
      jsonPost(input),
    ),
  invoicePdf: (
    token: string,
    id: string,
    invoiceId: string,
    disposition: "inline" | "attachment" = "attachment",
    signal?: AbortSignal,
  ) =>
    requestBlob(
      `${paymentInvoice(id, invoiceId)}/pdf${queryString({ disposition })}`,
      token,
      { signal },
    ),
  recordPayment: (
    token: string,
    id: string,
    invoiceId: string,
    input: PaymentRecordInput,
  ) =>
    request<PaymentCommandResultDTO>(
      `${paymentInvoice(id, invoiceId)}/payments`,
      token,
      jsonPost(input),
    ),
  correctPayment: (
    token: string,
    id: string,
    paymentId: string,
    input: PaymentCorrectionInput,
  ) =>
    request<PaymentCommandResultDTO>(
      `${paymentRecord(id, paymentId)}/corrections`,
      token,
      jsonPost(input),
    ),
  previewInvoiceApplication: (
    token: string,
    id: string,
    invoiceId: string,
    input: InvoiceApplicationPreviewInput,
  ) =>
    request<InvoiceApplicationPreviewDTO>(
      `${paymentInvoice(id, invoiceId)}/application/preview`,
      token,
      jsonPost(input),
    ),
  confirmInvoiceApplication: (
    token: string,
    id: string,
    invoiceId: string,
    input: InvoiceApplicationConfirmInput,
  ) =>
    request<PaymentCommandResultDTO>(
      `${paymentInvoice(id, invoiceId)}/application/confirm`,
      token,
      jsonPost(input),
    ),
  previewInvoiceUpgrade: (
    token: string,
    id: string,
    invoiceId: string,
    input: InvoiceUpgradePreviewInput,
  ) =>
    request<InvoiceApplicationPreviewDTO>(
      `${paymentInvoice(id, invoiceId)}/upgrade/preview`,
      token,
      jsonPost(input),
    ),
  confirmInvoiceUpgrade: (
    token: string,
    id: string,
    invoiceId: string,
    input: InvoiceApplicationConfirmInput,
  ) =>
    request<PaymentCommandResultDTO>(
      `${paymentInvoice(id, invoiceId)}/upgrade/confirm`,
      token,
      jsonPost(input),
    ),
  previewPaymentReview: (
    token: string,
    id: string,
    invoiceId: string,
    input: PaymentReviewPreviewInput,
  ) =>
    request<PaymentReviewPreviewDTO>(
      `${paymentInvoice(id, invoiceId)}/reviews/preview`,
      token,
      jsonPost(input),
    ),
  confirmPaymentReview: (
    token: string,
    id: string,
    invoiceId: string,
    input: PaymentReviewConfirmInput,
  ) =>
    request<PaymentReviewResultDTO>(
      `${paymentInvoice(id, invoiceId)}/reviews`,
      token,
      jsonPost(input),
    ),
  uploadPaymentReceipt: (
    token: string,
    id: string,
    paymentId: string,
    input: PaymentReceiptInput,
    file: File,
  ) => {
    const body = new FormData();
    body.append("metadata", serializePaymentReceiptMetadata(input));
    body.append("file", file);
    return request<{ paymentId: string; document: PaymentDocumentDTO }>(
      `${paymentRecord(id, paymentId)}/receipts`,
      token,
      { method: "POST", body },
    );
  },
  paymentReceipts: (
    token: string,
    id: string,
    paymentId: string,
    query: PaymentPageQuery = {},
    signal?: AbortSignal,
  ) =>
    request<CursorPage<PaymentDocumentDTO>>(
      `${paymentRecord(id, paymentId)}/receipts${queryString({ ...query })}`,
      token,
      { signal },
    ),
  paymentReceiptPdf: (
    token: string,
    id: string,
    paymentId: string,
    documentId: string,
    signal?: AbortSignal,
  ) =>
    requestBlob(
      `${paymentRecord(id, paymentId)}/receipts/${pathSegment(documentId)}`,
      token,
      { signal },
    ),
  paymentRecipient: (token: string, signal?: AbortSignal) =>
    request<{ activeVersion: PaymentRecipientVersionDTO | null }>(
      "/admin/payment-recipient",
      token,
      { signal },
    ),
  paymentRecipientVersions: (
    token: string,
    query: PaymentPageQuery = {},
    signal?: AbortSignal,
  ) =>
    request<CursorPage<PaymentRecipientVersionDTO>>(
      `/admin/payment-recipient/versions${queryString({ ...query })}`,
      token,
      { signal },
    ),
  previewPaymentRecipient: (
    token: string,
    input: PaymentRecipientPreviewInput,
  ) =>
    request<PaymentRecipientPreviewDTO>(
      "/admin/payment-recipient/preview",
      token,
      jsonPost(input),
    ),
  paymentRecipientPreviewPdf: (
    token: string,
    input: { previewToken: string },
  ) =>
    requestBlob("/admin/payment-recipient/preview/pdf", token, jsonPost(input)),
  confirmPaymentRecipient: (
    token: string,
    input: PaymentRecipientConfirmInput,
  ) =>
    request<{ activeVersion: PaymentRecipientVersionDTO }>(
      "/admin/payment-recipient/confirm",
      token,
      jsonPost(input),
    ),
  tariffs: (token: string) => request<TariffCatalogDTO>("/admin/tariffs", token),
  previewTariffs: (token: string, input: TariffCatalogPreviewInput) =>
    request<TariffCatalogPreviewDTO>("/admin/tariffs/preview", token, {
      method: "POST", body: JSON.stringify(input),
    }),
  confirmTariffs: (token: string, input: TariffCatalogConfirmInput) =>
    request<TariffCatalogDTO>("/admin/tariffs/confirm", token, {
      method: "POST", body: JSON.stringify(input),
    }),
  accountBilling: (token: string, id: string) =>
    request<AdminBillingViewDTO>(`/admin/accounts/${pathSegment(id)}/billing`, token),
  previewBillingTariffChange: (token: string, id: string, input: BillingTariffChangeInput) =>
    request<BillingTariffChangePreviewDTO>(`/admin/accounts/${pathSegment(id)}/billing/change/preview`, token, {
      method: "POST", body: JSON.stringify(input),
    }),
  confirmBillingTariffChange: (token: string, id: string, input: BillingConfirmInput) =>
    request<BillingTariffChangeResultDTO>(`/admin/accounts/${pathSegment(id)}/billing/change/confirm`, token, {
      method: "POST", body: JSON.stringify(input),
    }),
  previewBilling: (token: string, id: string, input: BillingPreviewInput) =>
    request<BillingPreviewDTO>(`/admin/accounts/${pathSegment(id)}/billing/preview`, token, {
      method: "POST", body: JSON.stringify(input),
    }),
  confirmBilling: (token: string, id: string, input: BillingConfirmInput) =>
    request<BillingConfirmResultDTO>(`/admin/accounts/${pathSegment(id)}/billing/confirm`, token, {
      method: "POST", body: JSON.stringify(input),
    }),
  wabaTemplates: (token: string, id: string, channelId: string) =>
    request<WabaTemplateListDTO>(
      `/admin/funnels/${encodeURIComponent(id)}/waba-templates?channelId=${encodeURIComponent(channelId)}`,
      token,
    ),
  previewFollowup: (
    token: string,
    id: string,
    input: FollowupPreviewInput & { expectedUpdatedAt: string },
    signal: AbortSignal,
  ) =>
    request<FollowupPreviewDTO>(
      `/admin/funnels/${encodeURIComponent(id)}/followups/preview`,
      token,
      { method: "POST", body: JSON.stringify(input), signal },
    ),
  getFollowups: (token: string, id: string) =>
    request<FollowupSettingsDTO>(
      `/admin/funnels/${encodeURIComponent(id)}/followups`,
      token,
    ),
  saveFollowups: (
    token: string,
    id: string,
    input: SaveFollowups & { expectedUpdatedAt: string },
  ) =>
    request<FollowupSettingsDTO>(
      `/admin/funnels/${encodeURIComponent(id)}/followups`,
      token,
      { method: "PUT", body: JSON.stringify(input) },
    ),

  listAgentActions: (token: string, id: string, cursor?: string) =>
    request<CursorPage<AgentActionDTO>>(
      `/admin/funnels/${encodeURIComponent(id)}/actions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      token,
    ),
  reconcileAgentAction: (token: string, id: string) =>
    request<AgentActionDTO>(
      `/admin/agent-actions/${encodeURIComponent(id)}/reconcile`,
      token,
      { method: "POST", body: JSON.stringify({}) },
    ),
  promptCapabilities: (token: string, accountId: string) =>
    request<{ promptEditorEnabled: boolean }>(
      `/admin/accounts/${accountId}/prompt-builder/capabilities`,
      token,
    ),
  generatePromptCandidate: (
    token: string,
    accountId: string,
    input: PromptBuilderInput,
    signal?: AbortSignal,
  ) =>
    request<PromptGenerationCandidate>(
      `/admin/accounts/${accountId}/prompt-builder/generate`,
      token,
      { method: "POST", body: JSON.stringify(input), signal },
    ),
  generateEscalation: (
    token: string,
    accountId: string,
    input: EscalationGenerationInput,
    signal?: AbortSignal,
  ) =>
    request<EscalationGenerationCandidate>(
      `/admin/accounts/${accountId}/prompt-builder/escalation`,
      token,
      { method: "POST", body: JSON.stringify(input), signal },
    ),
  platformPrompts: (token: string) =>
    request<{ items: PlatformPromptListItem[] }>(
      "/admin/settings/prompts",
      token,
    ),
  platformPrompt: (token: string, key: PlatformPromptKey) =>
    request<PromptSnapshot>(
      `/admin/settings/prompts/${pathSegment(key)}`,
      token,
    ),
  platformPromptRevisions: (
    token: string,
    key: PlatformPromptKey,
    query: CursorQuery = {},
  ) =>
    request<PlatformPromptRevisionPage>(
      `/admin/settings/prompts/${pathSegment(key)}/revisions${queryString(query)}`,
      token,
    ),
  savePlatformPrompt: (
    token: string,
    key: PlatformPromptKey,
    data: { content: string; expectedVersion: number },
  ) =>
    request<PromptSnapshot>(
      `/admin/settings/prompts/${pathSegment(key)}`,
      token,
      { method: "PUT", body: JSON.stringify(data) },
    ),
  restorePlatformPrompt: (
    token: string,
    key: PlatformPromptKey,
    data: { revisionId: string; expectedVersion: number },
  ) =>
    request<PromptSnapshot>(
      `/admin/settings/prompts/${pathSegment(key)}/restore`,
      token,
      { method: "POST", body: JSON.stringify(data) },
    ),
  login: (data: { username: string; password: string }) =>
    request<AuthResponse>("/admin/auth/login", null, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  register: (data: { username: string; password: string; inviteKey: string }) =>
    request<AuthResponse>("/admin/auth/register", null, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  me: (token: string) => request<AdminUser>("/admin/auth/me", token),

  changePassword: (
    token: string,
    data: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    },
  ) =>
    request<void>("/admin/auth/change-password", token, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  accounts,

  crmFields: (token: string, id: string, entity: "lead" | "contact") =>
    request<CrmCatalogPreview>(
      `/admin/accounts/${pathSegment(id)}/amo/fields?entity=${entity}`,
      token,
    ),
  updateAccountPreferences: (
    token: string,
    id: string,
    data: { timeZone: string; expectedSettingsVersion: number },
  ) =>
    request<{ timeZone: string; settingsVersion: number }>(
      `/admin/accounts/${pathSegment(id)}/preferences`,
      token,
      { method: "PATCH", body: JSON.stringify(data) },
    ),
  account: (token: string, id: string) =>
    request<AdminAccount>(`/admin/accounts/${pathSegment(id)}`, token),

  updateProfile: (token: string, id: string, data: UpdateAccountProfileInput) =>
    request<AdminAccountUser>(
      `/admin/accounts/${pathSegment(id)}/profile`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    ),

  suspendAccount: (token: string, id: string, data: SuspendAccountInput) =>
    request<AdminAccountSettings>(
      `/admin/accounts/${pathSegment(id)}/suspend`,
      token,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  resumeAccount: (
    token: string,
    id: string,
    data: ExpectedAccountVersionInput,
  ) =>
    request<AdminAccountSettings>(
      `/admin/accounts/${pathSegment(id)}/resume`,
      token,
      { method: "POST", body: JSON.stringify(data) },
    ),

  setTemporaryPassword: (
    token: string,
    id: string,
    data: TemporaryPasswordInput,
  ) =>
    request<void>(
      `/admin/accounts/${pathSegment(id)}/user/temp-password`,
      token,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  integrationStatus: (token: string, id: string) =>
    request<AdminIntegrationStatus>(
      `/admin/accounts/${pathSegment(id)}/integrations/status`,
      token,
    ),

  amoPipelines: (token: string, id: string) =>
    request<AdminPipeline[]>(
      `/admin/accounts/${pathSegment(id)}/amo/pipelines`,
      token,
    ),

  amoPipelineStatuses: (token: string, id: string, pipelineId: string) =>
    request<AdminPipelineStatus[]>(
      `/admin/accounts/${pathSegment(id)}/amo/pipelines/${pathSegment(pipelineId)}/statuses`,
      token,
    ),

  wazzupChannels: (token: string, id: string) =>
    request<AdminWazzupChannel[]>(
      `/admin/accounts/${pathSegment(id)}/wazzup/channels`,
      token,
    ),

  accountFunnels: (token: string, id: string) =>
    request<AdminFunnel[]>(`/admin/accounts/${pathSegment(id)}/funnels`, token),

  createFunnel: (
    token: string,
    accountId: string,
    data: CreateAdminFunnelInput,
  ) =>
    request<AdminFunnelSettings>(
      `/admin/accounts/${pathSegment(accountId)}/funnels`,
      token,
      { method: "POST", body: JSON.stringify(data) },
    ),

  updateFunnel: (token: string, id: string, data: UpdateAdminFunnelInput) =>
    request<AdminFunnelSettings>(`/admin/funnels/${pathSegment(id)}`, token, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteFunnel: (token: string, id: string, data: DeleteAdminFunnelInput) =>
    request<void>(`/admin/funnels/${pathSegment(id)}`, token, {
      method: "DELETE",
      body: JSON.stringify(data),
    }),

  testFunnel: (token: string, id: string, data: TestAdminFunnelInput) =>
    request<TestAdminFunnelResult>(
      `/admin/funnels/${pathSegment(id)}/test`,
      token,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  bindChannel: (token: string, funnelId: string, data: BindAdminChannelInput) =>
    request<AdminChannel>(
      `/admin/funnels/${pathSegment(funnelId)}/channels`,
      token,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  unbindChannel: (token: string, funnelId: string, channelId: string) =>
    request<void>(
      `/admin/funnels/${pathSegment(funnelId)}/channels/${pathSegment(channelId)}`,
      token,
      { method: "DELETE" },
    ),

  dialogs: (token: string, accountId: string, query: AdminDialogsQuery = {}) =>
    request<CursorPage<AdminDialogListItem>>(
      `/admin/accounts/${pathSegment(accountId)}/dialogs${queryString(query)}`,
      token,
    ),

  dialogMessages: (
    token: string,
    accountId: string,
    dialogId: string,
    query: CursorQuery & { funnelId?: string } = {},
  ) =>
    request<CursorPage<AdminMessage>>(
      `/admin/accounts/${pathSegment(accountId)}/dialogs/${pathSegment(dialogId)}/messages${queryString(query)}`,
      token,
    ),

  audit: (token: string, accountId: string, query: CursorQuery = {}) =>
    request<CursorPage<AdminAuditEntry>>(
      `/admin/accounts/${pathSegment(accountId)}/audit${queryString(query)}`,
      token,
    ),

  funnels: (token: string) => request<AdminFunnel[]>("/admin/funnels", token),

  funnel: (token: string, id: string) =>
    request<AdminFunnel>(`/admin/funnels/${pathSegment(id)}`, token),

  funnelRuns: (token: string, id: string, query: AdminRunsQuery = {}) =>
    request<PipelineRun[]>(
      `/admin/funnels/${pathSegment(id)}/runs${queryString(query)}`,
      token,
    ),

  runs: (
    token: string,
    query: AdminRunsQuery & { accountId?: string; funnelId?: string } = {},
  ) => request<PipelineRun[]>(`/admin/runs${queryString(query)}`, token),

  run: (token: string, id: string) =>
    request<PipelineRunDetail>(`/admin/runs/${pathSegment(id)}`, token),
};
